import type { QueryFamily } from "./keys";

/**
 * Which query families a written table feeds. Read by the live feed: a `revision`
 * event names the tables its transaction wrote (core's `write()` notes them), and
 * only the families listed here are refetched — and of those, only the queries a
 * view is showing; the rest are marked stale for their next mount.
 *
 * A table that is not here invalidates everything. That is deliberate: a new
 * table that someone forgot to map costs a refetch, never a stale screen.
 */
export const TABLE_FAMILIES: Record<string, readonly QueryFamily[]> = {
  // Cards, and every view that lists them.
  boards: ["boards", "queue", "awaiting", "project-usage", "canvas"],
  board_columns: ["boards", "queue", "awaiting", "canvas"],
  board_projects: ["boards", "project-usage", "projects"],
  // A canvas shows its linked cards' live state, so a card change refetches it too.
  tasks: ["boards", "queue", "awaiting", "task", "project-usage", "canvas"],
  // A canvas board's graph; a card's "On canvases" row reads the nodes too.
  canvas_nodes: ["canvas", "task"],
  canvas_edges: ["canvas"],
  task_comments: ["task", "awaiting"],
  comment_attachments: ["task"],
  task_mentions: ["boards", "task", "awaiting"],
  task_media: ["task"],
  task_responses: ["boards", "task", "response", "awaiting"],
  response_turns: ["boards", "task", "response", "awaiting", "status"],
  activity: ["activity"],

  // Pasted and imported material: the panel, and the board's counts in its menu.
  intake_messages: ["intake", "boards", "status"],
  intake_attachments: ["intake"],
  whatsapp_chats: ["whatsapp", "boards"],
  whatsapp_imports: ["whatsapp", "boards", "status"],
  whatsapp_media: ["whatsapp", "task"],
  whatsapp_messages: ["whatsapp", "task"],

  // Directories: the pickers, the Projects page, and every render that resolves one.
  projects: ["projects", "project-usage", "boards", "agents", "queue", "task"],

  // People and agents. An agent is also a users row.
  users: ["users", "agents", "notifications"],
  agents: ["agents", "status", "awaiting", "project-usage"],
  agent_turns: ["agents", "agent-turns", "status"],
  agent_messages: ["agents", "awaiting"],
  agent_attachments: [],
  agent_triggers: ["agents"],
  agent_folders: ["agents"],
  // A foreign agent's link to its machine: paired, online, which machine and directory.
  agent_links: ["agents", "status"],
  // The registry of MCP servers, and which agent carries which.
  mcp_servers: ["agents", "mcp-servers"],
  agent_mcp_servers: ["agents"],

  // What only the worker writes.
  claude_usage: ["status"],
  status_snapshots: ["status", "apps", "claude-accounts", "remote-machines", "engine-settings"],

  // Saved window layouts: the Windows menu's list.
  workspace_layouts: ["layouts"],

  // Agents' dev-server previews (hits are written outside write(), so they never ring).
  previews: ["previews"],
  remote_machines: ["remote-machines"],
  preview_grants: [],

  // Nothing any view reads.
  meta: [],
  sessions: [],
  invites: [],
  mcp_tokens: ["mcp-tokens"],
  push_subscriptions: ["notifications"],
};

/**
 * Families the live feed never invalidates. A transcript is kept current by
 * `agent.message` events written straight into its cache (and re-read on a
 * reconnect); refetching an infinite query would re-read every page. The voice
 * engines and a folder listing do not change with the board.
 */
export const LIVE_EXEMPT: ReadonlySet<QueryFamily> = new Set(["agent-messages", "voice", "browse"]);

/** "Everything" — an event that cannot say what it touched. */
export type Invalidation = { all: true } | { all: false; families: Set<QueryFamily> };

/**
 * The families one `revision` event invalidates. No `tables` (an older process
 * published it, or the poll saw the counter move) means everything; so does a
 * `"*"` or a table not in the map.
 */
export function invalidationFor(tables: readonly string[] | undefined): Invalidation {
  if (!tables) return { all: true };
  const families = new Set<QueryFamily>();
  for (const table of tables) {
    const fed = TABLE_FAMILIES[table];
    if (!fed) return { all: true };
    for (const family of fed) families.add(family);
  }
  return { all: false, families };
}

/** Two invalidations as one: what a batch of events adds up to. */
export function mergeInvalidations(a: Invalidation | null, b: Invalidation): Invalidation {
  if (!a) return b;
  if (a.all || b.all) return { all: true };
  return { all: false, families: new Set([...a.families, ...b.families]) };
}

/** Whether a query of `family` should be refetched for `invalidation`. */
export function invalidates(invalidation: Invalidation, family: QueryFamily | null): boolean {
  if (family === null) return invalidation.all;
  if (LIVE_EXEMPT.has(family)) return false;
  return invalidation.all || invalidation.families.has(family);
}
